import { useRef, useState, useEffect } from "react";

const OtpInput = ({ length = 6, setVerificationCode }) => {
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const inputRefs = useRef([]);

  // Handle input changes (handles typing AND Chrome autofill)
  const handleChange = (e, index) => {
    const value = e.target.value;
    if (!value) return;

    // Check if the input is just one character or a full code (Chrome autofill)
    // We strictly extract numbers only
    const cleanVal = value.replace(/[^0-9]/g, "");

    if (cleanVal.length > 1) {
      // --- AUTOFILL SCENARIO (e.g. Chrome on iOS) ---
      const newOtp = [...otp];
      const splitVal = cleanVal.split("");

      // Distribute the characters starting from the current index
      splitVal.forEach((char, i) => {
        if (index + i < length) {
          newOtp[index + i] = char;
        }
      });

      setOtp(newOtp);
      setVerificationCode(newOtp.join(""));

      // Focus the input after the last one we just filled
      const nextIndex = Math.min(index + splitVal.length, length - 1);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
    } else {
      // --- TYPING SCENARIO (Single digit) ---
      const newOtp = [...otp];
      newOtp[index] = cleanVal;
      setOtp(newOtp);
      setVerificationCode(newOtp.join(""));

      // Move focus to next input
      if (cleanVal && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
        setVerificationCode(newOtp.join(""));
      } else if (index > 0) {
        inputRefs.current[index - 1].focus();
        newOtp[index - 1] = "";
        setOtp(newOtp);
        setVerificationCode(newOtp.join(""));
      }
    }
  };

  // This handles manual copy-paste (Long press -> Paste)
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, length);
    const newOtp = [...otp];
    for (let i = 0; i < length; i++) {
      if (pasteData[i] && !isNaN(pasteData[i])) {
        newOtp[i] = pasteData[i];
      }
    }
    setOtp(newOtp);
    setVerificationCode(newOtp.join(""));

    // Focus the last filled input
    const lastIndex = Math.min(pasteData.length, length - 1);
    if (inputRefs.current[lastIndex]) {
      inputRefs.current[lastIndex].focus();
    }
  };

  return (
    <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
      {otp.map((data, index) => (
        <input
          key={index}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code" // <--- CRITICAL FOR CHROME
          maxLength={index === 0 ? length : 1} // <--- TRICK: Allow first input to accept long strings for autofill capture
          value={data}
          ref={(el) => (inputRefs.current[index] = el)}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
        />
      ))}
    </div>
  );
};

export default OtpInput;
